import { createAction } from 'redux-starter-kit';
import { notification } from 'antd';
import { get, post, request } from '../../../../utility/via-fetch';
import { requestStart, requestFailed, requestSucceded } from '../../../domain/requests/RequestActions';
import { addMessage } from '../../application/messageUiActions';
import { putUser, putUsers, removeUser } from '../../../domain/users/userActions';
import { putAppConfig } from '../../../domain/appConfig/appConfigActions';
import {roleNames} from "../../../../sections/secured/configure/company/user-util";

/**
 * An action that signifies a user is being currently edited.
 * @example dispatch(editUser(user))
 */
export const editUser = createAction('EDIT_USER');

/**
 * An action that signifies A user is no longer being edited.
 * @example dispatch(cancelEditUser())
 */
export const cancelEditUser = createAction('CANCEL_EDIT_USER');

/**
 * An action that signifies a request to load all users has been made.
 * @example dispatch(fetchUsers())
 */
export const fetchUsers = createAction('FETCH_USERS');

/**
 * An action for loading all the users.
 * @example dispatch(requestUsers())
 */
export function requestUsers() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchUsers.toString()));
    dispatch(fetchUsers(requestId));

    try {
      const users = await get('/api/users/');

      //remove read only for role processing on screen - will be added again on EJB save
      for (let usersKey in users) {
        if (users[usersKey] && users[usersKey].linkedRoles) {
          const roleIndex = users[usersKey].linkedRoles.indexOf(roleNames.READ_ONLY);
          if (roleIndex > -1) {
            users[usersKey].linkedRoles.splice(roleIndex, 1);
          }
        }
      }

      dispatch(putUsers(users));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies that a fetch to add a new user has been
 * made in the user configuration screen. This should pass through the request id.
 * @example dispatch(fetchAddUser(requestId))
 */
export const fetchAddUser = createAction('FETCH_CREATE_USER');

/**
 * An action for creating an user on the server. Will lead to an email being sent to the new users email address to set their password.
 * @param {object} user The user to be created.
 * @param {intl} intl The intl instance being used in the application as retrieved by injectintl
 * @example dispatch(requestAddUser(user));
 */
export function requestAddUser(user, intl) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchAddUser.toString()));
    dispatch(fetchAddUser(requestId));
    try {
      const userId = await post('/api/users', {}, user);
      dispatch(putUser({ ...user, userId: userId.value, locale: 'en_GB' }));
      dispatch(cancelEditUser());
      dispatch(requestSucceded(requestId, user.username));
      notification.success({
        message: intl.formatMessage({ id: 'invitationEmailSent' }),
        description: intl.formatMessage({ id: 'emailSentTo' }) + user.username + intl.formatMessage({ id: 'emailAction' }),
        duration: 10,
      });
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies that a fetch to update a user
 * has been made in the users configuration screen.
 * This should pass through the request id.
 * @example dispatch(fetchUpdateUser(requestId));
 */
export const fetchUpdateUser = createAction('FETCH_UPDATE_USER');

/**
 * An action for updating an user on the server.
 * @param {object} user The user to be updated.
 */
export function requestUpdateUser(user) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchUpdateUser.toString()));
    dispatch(fetchUpdateUser(requestId));
    try {
      await post(`/api/users/${user.username}`, {}, user);
      dispatch(putUser(user));
      dispatch(cancelEditUser());
      dispatch(requestSucceded(requestId, user.username));
      dispatch(addMessage('success', 'itemUpdated', { item: user.displayName }));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies that a fetch to update the user
 * has been made in the my details configuration screen.
 * This should pass through the request id.
 * @example dispatch(fetchUpdateSelf(requestId));
 */
export const fetchUpdateSelf = createAction('FETCH_UPDATE_SELF');

/**
 * An action for updating an user on the server.
 * @param {object} user The user to be updated.
 */
export function requestUpdateSelf(user) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchUpdateSelf.toString()));
    dispatch(fetchUpdateSelf(requestId));
    try {
      await post('/api/users/my-details', {}, user);
      dispatch(putUser(user));
      dispatch(putAppConfig({ applicationConfigCode: 'countries', countries: null }));
      dispatch(requestSucceded(requestId));
      dispatch(addMessage('success', 'itemUpdated', { item: user.displayName }));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies that a fetch to delete a user
 * has been made in the users configuration screen.
 * This should pass through the request id.
 * @example dispatch(deleteUser(requestId));
 */
export const deleteUser = createAction('DELETE_USER');

/**
 * An action for deleting a user on the server.
 * @param {object} user The user to be updated.
 */
export function requestDeleteUser(userId, username) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(deleteUser.toString()));
    dispatch(deleteUser(requestId));
    try {
      await request('DELETE', `/api/users/${username}`, {}, {});
      dispatch(removeUser(userId));
      dispatch(cancelEditUser());
      dispatch(requestSucceded(requestId));
      dispatch(addMessage('success', 'itemDeleted', { item: username }));
    } catch (error) {
      dispatch(requestUsers());
      dispatch(requestFailed(requestId, error));
    }
  };
}
